import React from "react";
import {
  Box,
  Typography,
  Switch,
  useTheme,
  TextField,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import TooltipDialogCst from "@components/TooltipDialogCst";

interface SwitchCstInterface {
  id: string;
  label: string;
  textTooltip?: string;
  disabled?: boolean;
  value: boolean;
  error?: boolean;
  errorMessage?: string;
  onChange?: (value: any) => void;
  disabledTooltip?: string;
  testIdPrefix?: string;
  ariaLabelOverride?: string;
}

const SwitchCst: React.FC<SwitchCstInterface> = ({
  id,
  label,
  textTooltip,
  disabled,
  value,
  error,
  errorMessage,
  onChange,
  disabledTooltip,
  testIdPrefix,
  ariaLabelOverride,
}) => {
  const theme = useTheme();

  const switchTestId = testIdPrefix ? `${testIdPrefix}-switch` : undefined;

  return (
    <Tooltip
      title={disabledTooltip}
      arrow
      placement="top"
      slotProps={{
        tooltip: {
          sx: { maxWidth: "480px", whiteSpace: "nowrap" },
        },
      }}
    >
      <Box
        sx={{
          minWidth: "200px",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "auto",
          ".MuiFormControl-root": {},
          ".MuiAutocomplete-endAdornment": {
            display: "none",
          },
          ".MuiFormLabel-root": {
            maxWidth: "calc(100% - 65px)",
            "&[data-shrink='true']": {
              maxWidth: "calc(130% - 40px)",
            },
          },
          ".MuiInputBase-root": {
            flexWrap: "nowrap !important",
            marginLeft: "0px",
          },
          ".MuiInputBase-input": {
            scrollMargin: "250px",
          },
        }}
      >
        {textTooltip && (
          <TooltipDialogCst
            inputDialog
            title={label}
            error={error}
            textTooltip={textTooltip}
          />
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            position: "absolute",
            right: "5px",
            top: "0px",
            zIndex: "100",
            cursor: "pointer",
            color: disabled
              ? theme.palette.text.disabled
              : theme.palette.text.primary,
          }}
        >
          <Switch
            checked={value}
            onChange={(event) => onChange && onChange(event)}
            disabled={disabled}
            color="primary"
            {...(switchTestId ? { "data-testid": switchTestId } : {})}
            inputProps={{
              ...(switchTestId
                ? { "data-testid": `${switchTestId}-input` }
                : {}),
              ...(ariaLabelOverride ? { "aria-label": ariaLabelOverride } : {}),
            }}
          />
        </Box>

        <Autocomplete
          multiple
          id={id}
          options={[]}
          sx={{ width: "100%" }}
          limitTags={1}
          disabled={disabled}
          readOnly
          freeSolo={true}
          renderTags={(values) => {
            return (
              <Box
                sx={{
                  mt: "4px",
                  ml: textTooltip ? 3 : 0,
                  display: "flex",
                  alignItems: "center",
                  width: "90%",
                  gap: "5px",
                  flexWrap: "wrap",
                  "& + input": { height: "0px" },
                }}
              >
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {label}
                </Typography>
              </Box>
            );
          }}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                color: theme.palette.text.primary,
                ".Mui-error": {
                  color: error
                    ? theme.palette.error.main
                    : theme.palette.text.primary,
                },
              }}
              label={label}
              error={error}
              helperText={error ? errorMessage : ""}
            />
          )}
        />
      </Box>
    </Tooltip>
  );
};

export default SwitchCst;
